import { isGradientGeometryV2 } from '@web-stories-wp/patterns';
import DOMPurify from 'dompurify';

const MAX_RICH_TEXT_LENGTH = 200_000;
const MAX_INLINE_SVG_LENGTH = 2_000_000;
const MAX_EXTERNAL_STICKER_URL_LENGTH = 2_048;
const MAX_STYLE_LENGTH = 20_000;
const MAX_STYLE_VALUE_LENGTH = 16_000;
const MAX_GRADIENT_STOPS = 64;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';
const XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace';
const XMLNS_NAMESPACE = 'http://www.w3.org/2000/xmlns/';

const ALLOWED_RICH_TEXT_ELEMENTS = ['br', 'li', 'ol', 'p', 'span', 'ul'];
const FONT_REFERENCE_ATTRIBUTE = 'data-st-font-ref';
const FONT_SIZE_ATTRIBUTE = 'data-st-font-size';
const LINE_HEIGHT_ATTRIBUTE = 'data-st-line-height';

const ALLOWED_RICH_TEXT_STYLE_PROPERTIES = new Set([
  '--gradient-pattern',
  '--ws-text-gradient',
  '-webkit-background-clip',
  '-webkit-text-fill-color',
  'background-clip',
  'background-image',
  'color',
  'font-style',
  'font-weight',
  'letter-spacing',
  'text-decoration',
  'text-transform',
]);

const ALLOWED_SVG_ELEMENTS = [
  'circle',
  'clippath',
  'defs',
  'desc',
  'ellipse',
  'feblend',
  'fecolormatrix',
  'fecomponenttransfer',
  'fecomposite',
  'feconvolvematrix',
  'fediffuselighting',
  'fedisplacementmap',
  'fedistantlight',
  'fedropshadow',
  'feflood',
  'fefunca',
  'fefuncb',
  'fefuncg',
  'fefuncr',
  'fegaussianblur',
  'feimage',
  'femerge',
  'femergenode',
  'femorphology',
  'feoffset',
  'fepointlight',
  'fespecularlighting',
  'fespotlight',
  'fetile',
  'feturbulence',
  'filter',
  'g',
  'image',
  'line',
  'lineargradient',
  'mask',
  'path',
  'pattern',
  'polygon',
  'polyline',
  'radialgradient',
  'rect',
  'stop',
  'svg',
  'symbol',
  'text',
  'title',
  'tspan',
  'use',
];
const ALLOWED_SVG_ELEMENT_SET = new Set(ALLOWED_SVG_ELEMENTS);
const EXTERNAL_SVG_RESOURCE_ELEMENTS = new Set(['image', 'feimage']);

const ALLOWED_SVG_STYLE_PROPERTIES = new Set([
  'clip-path',
  'clip-rule',
  'color',
  'color-interpolation',
  'color-interpolation-filters',
  'display',
  'fill',
  'fill-opacity',
  'fill-rule',
  'filter',
  'flood-color',
  'flood-opacity',
  'isolation',
  'lighting-color',
  'mask',
  'mix-blend-mode',
  'opacity',
  'stop-color',
  'stop-opacity',
  'stroke',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'transform',
  'transform-origin',
  'vector-effect',
  'visibility',
]);

const NUMERIC_CSS_VALUE = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;
const CSS_FUNCTION_COLOR = /^(?:rgb|rgba|hsl|hsla)\([0-9a-z.,%/ +\-]+\)$/i;
const CSS_NAMED_COLOR = /^[a-z]+$/i;
const CSS_CUSTOM_COLOR_NAME = /^--custom-color-[a-z0-9-]+$/i;
const CSS_GRADIENT =
  /^(?:repeating-)?(?:linear|radial)-gradient\([#(),.%0-9a-z +\-]+\)$/i;
const CSS_URL_FUNCTION = /(?:^|[^a-z0-9_-])url\s*\(/i;
const LOCAL_CSS_URL_VALUE = /^url\s*\(\s*(['"]?)(#[a-z0-9_.:-]+)\1\s*\)$/i;
const LOCAL_FRAGMENT = /^#[a-z0-9_.:-]+$/i;
const RASTER_DATA_URL =
  /^data:image\/(?:png|jpeg|gif|webp);base64,[a-z0-9+/=\s]+$/i;
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;
const DISALLOWED_XML_CONTROL_CHARACTER =
  /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

const RICH_TEXT_CONFIG = {
  ALLOWED_TAGS: ALLOWED_RICH_TEXT_ELEMENTS,
  ALLOWED_ATTR: [
    'style',
    FONT_REFERENCE_ATTRIBUTE,
    FONT_SIZE_ATTRIBUTE,
    LINE_HEIGHT_ATTRIBUTE,
  ],
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
  RETURN_DOM_FRAGMENT: true,
};

const SVG_CONFIG = {
  ALLOWED_TAGS: ALLOWED_SVG_ELEMENTS,
  ADD_DATA_URI_TAGS: ['image', 'feimage'],
  ALLOW_ARIA_ATTR: true,
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  KEEP_CONTENT: false,
  RETURN_DOM_FRAGMENT: true,
};

function canSanitizeMarkup() {
  return (
    typeof document !== 'undefined' &&
    DOMPurify &&
    DOMPurify.isSupported !== false
  );
}

function parseStyleDeclarations(style) {
  if (typeof style !== 'string' || style.length > MAX_STYLE_LENGTH) {
    return null;
  }

  if (!style.trim()) {
    return [];
  }

  const declarations = [];
  const properties = new Set();

  for (const segment of style.split(';')) {
    if (!segment.trim()) {
      continue;
    }

    const delimiterIndex = segment.indexOf(':');
    if (delimiterIndex <= 0 || delimiterIndex === segment.length - 1) {
      return null;
    }

    const property = segment.slice(0, delimiterIndex).trim().toLowerCase();
    const value = segment.slice(delimiterIndex + 1).trim();
    if (
      !property ||
      !value ||
      value.length > MAX_STYLE_VALUE_LENGTH ||
      properties.has(property)
    ) {
      return null;
    }

    properties.add(property);
    declarations.push([property, value]);
  }

  return declarations;
}

function decodePercentEscapes(value) {
  let malformed = false;
  const decoded = value.replace(/(?:%[0-9a-f]{2})+/gi, (sequence) => {
    try {
      return decodeURIComponent(sequence);
    } catch (error) {
      malformed = true;
      return sequence;
    }
  });
  return malformed ? null : decoded;
}

function containsUnsafeMarkupToken(value, allowXmlWhitespace = false) {
  const controlCharacterPattern = allowXmlWhitespace
    ? DISALLOWED_XML_CONTROL_CHARACTER
    : CONTROL_CHARACTER;
  if (
    controlCharacterPattern.test(value) ||
    value.includes('\\') ||
    value.includes('/*')
  ) {
    return true;
  }

  let normalized = value;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const decoded = decodePercentEscapes(normalized);
    if (decoded === null) {
      return true;
    }
    if (decoded === normalized) {
      break;
    }
    normalized = decoded;
  }

  normalized = normalized.replace(/[\s\u0000-\u001f\u007f]/g, '').toLowerCase();
  return [
    'javascript:',
    'vbscript:',
    'data:text/html',
    'data:image/svg+xml',
    'expression(',
    '@import',
    '-moz-binding',
    'behavior:',
    'file:',
  ].some((token) => normalized.includes(token));
}

function isSafeCssValue(value) {
  return (
    value.length <= MAX_STYLE_VALUE_LENGTH &&
    !CONTROL_CHARACTER.test(value) &&
    !value.includes('\\') &&
    !value.includes('/*') &&
    !containsUnsafeMarkupToken(value)
  );
}

function isSafeCssColor(value) {
  if (value.startsWith('#')) {
    return (
      [4, 5, 7, 9].includes(value.length) && /^[0-9a-f]+$/i.test(value.slice(1))
    );
  }

  if (CSS_FUNCTION_COLOR.test(value) || CSS_NAMED_COLOR.test(value)) {
    return true;
  }

  if (!/^var\(/i.test(value) || !value.endsWith(')')) {
    return false;
  }

  const variableValue = value.slice(4, -1);
  const delimiterIndex = variableValue.indexOf(',');
  if (delimiterIndex < 0) {
    return false;
  }

  const variableName = variableValue.slice(0, delimiterIndex).trim();
  const fallback = variableValue.slice(delimiterIndex + 1).trim();
  return CSS_CUSTOM_COLOR_NAME.test(variableName) && isSafeCssColor(fallback);
}

function validateGradientStop(stop) {
  if (
    !stop ||
    typeof stop !== 'object' ||
    !Number.isFinite(stop.position) ||
    stop.position < 0 ||
    stop.position > 1 ||
    !stop.color ||
    typeof stop.color !== 'object'
  ) {
    return false;
  }

  const { r, g, b, a } = stop.color;
  return (
    Number.isFinite(r) &&
    r >= 0 &&
    r <= 255 &&
    Number.isFinite(g) &&
    g >= 0 &&
    g <= 255 &&
    Number.isFinite(b) &&
    b >= 0 &&
    b <= 255 &&
    (a === undefined || (Number.isFinite(a) && a >= 0 && a <= 1))
  );
}

function validateEncodedGradient(value) {
  if (value.length > MAX_STYLE_VALUE_LENGTH) {
    return false;
  }

  try {
    const gradient = JSON.parse(decodeURIComponent(value));
    return (
      gradient &&
      typeof gradient === 'object' &&
      ['linear', 'radial'].includes(gradient.type) &&
      Array.isArray(gradient.stops) &&
      gradient.stops.length >= 1 &&
      gradient.stops.length <= MAX_GRADIENT_STOPS &&
      gradient.stops.every(validateGradientStop) &&
      (gradient.geometryVersion === undefined || isGradientGeometryV2(gradient))
    );
  } catch (error) {
    return false;
  }
}

function isAllowedKeyword(value, ...allowedValues) {
  return allowedValues.some(
    (allowedValue) => value.toLowerCase() === allowedValue
  );
}

function validateFontWeight(value) {
  if (isAllowedKeyword(value, 'normal', 'bold', 'bolder', 'lighter')) {
    return true;
  }
  return /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 1_000;
}

function validateLetterSpacing(value) {
  if (isAllowedKeyword(value, 'normal') || value === '0') {
    return true;
  }

  const unit = value.toLowerCase().endsWith('rem')
    ? 'rem'
    : value.toLowerCase().endsWith('em')
    ? 'em'
    : value.toLowerCase().endsWith('px')
    ? 'px'
    : null;
  return Boolean(unit && NUMERIC_CSS_VALUE.test(value.slice(0, -unit.length)));
}

function validateRichTextStyleValue(property, value) {
  if (!isSafeCssValue(value)) {
    return false;
  }

  switch (property) {
    case '--gradient-pattern':
    case '--ws-text-gradient':
      return validateEncodedGradient(value);
    case '-webkit-background-clip':
    case 'background-clip':
      return isAllowedKeyword(value, 'text');
    case '-webkit-text-fill-color':
    case 'color':
      return isSafeCssColor(value);
    case 'background-image':
      return !CSS_URL_FUNCTION.test(value) && CSS_GRADIENT.test(value);
    case 'font-style':
      return isAllowedKeyword(value, 'normal', 'italic', 'oblique');
    case 'font-weight':
      return validateFontWeight(value);
    case 'letter-spacing':
      return validateLetterSpacing(value);
    case 'text-decoration':
      return isAllowedKeyword(
        value,
        'none',
        'underline',
        'line-through',
        'overline'
      );
    case 'text-transform':
      return isAllowedKeyword(
        value,
        'none',
        'uppercase',
        'lowercase',
        'capitalize'
      );
    default:
      return false;
  }
}

function sanitizeStyle(style, allowedProperties, validateValue) {
  const declarations = parseStyleDeclarations(style);
  if (!declarations) {
    return '';
  }

  return declarations
    .filter(
      ([property, value]) =>
        allowedProperties.has(property) && validateValue(property, value)
    )
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ');
}

function serializeFragment(fragment) {
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}

function parseInertMarkup(markup) {
  // Template contents neither activate markup nor fetch its subresources, and
  // passing a fragment keeps DOMPurify from reparsing the raw string.
  const template = document.createElement('template');
  template.innerHTML = markup;
  return template.content;
}

export function sanitizeRichText(content) {
  if (
    typeof content !== 'string' ||
    content.length > MAX_RICH_TEXT_LENGTH ||
    !canSanitizeMarkup()
  ) {
    return '';
  }

  const fragment = DOMPurify.sanitize(
    parseInertMarkup(content),
    RICH_TEXT_CONFIG
  );
  for (const element of fragment.querySelectorAll('*')) {
    if (element.localName.toLowerCase() !== 'span') {
      element.removeAttribute('style');
      continue;
    }

    const style = sanitizeStyle(
      element.getAttribute('style') || '',
      ALLOWED_RICH_TEXT_STYLE_PROPERTIES,
      validateRichTextStyleValue
    );
    if (style) {
      element.setAttribute('style', style);
    } else {
      element.removeAttribute('style');
    }

    if (element.hasAttribute(LINE_HEIGHT_ATTRIBUTE)) {
      const rawLineHeight = element.getAttribute(LINE_HEIGHT_ATTRIBUTE);
      const lineHeight = Number(rawLineHeight);
      if (
        !rawLineHeight?.trim() ||
        !Number.isFinite(lineHeight) ||
        lineHeight < 0 ||
        lineHeight > 10
      ) {
        element.removeAttribute(LINE_HEIGHT_ATTRIBUTE);
      }
    }
  }

  return serializeFragment(fragment);
}

function areOnlyLocalCssUrls(value) {
  let matchCount = 0;
  let allLocal = true;
  const remainder = value.replace(
    /url\s*\(\s*([^)]*?)\s*\)/gi,
    (match, rawUrl) => {
      matchCount += 1;
      const url = rawUrl.trim().replace(/^(['"])(.*)\1$/, '$2');
      if (!LOCAL_FRAGMENT.test(url)) {
        allLocal = false;
      }
      return '';
    }
  );

  return matchCount > 0 && allLocal && !CSS_URL_FUNCTION.test(remainder);
}

function validateSvgStyleValue(property, value) {
  if (property === 'mask') {
    return (
      ALLOWED_SVG_STYLE_PROPERTIES.has(property) &&
      isSafeCssValue(value) &&
      (isAllowedKeyword(value, 'none') || LOCAL_CSS_URL_VALUE.test(value))
    );
  }

  return (
    ALLOWED_SVG_STYLE_PROPERTIES.has(property) &&
    isSafeCssValue(value) &&
    (!CSS_URL_FUNCTION.test(value) || areOnlyLocalCssUrls(value))
  );
}

export function isSafeExternalStickerUrl(value) {
  if (
    typeof value !== 'string' ||
    !value.trim() ||
    value.length > MAX_EXTERNAL_STICKER_URL_LENGTH
  ) {
    return false;
  }

  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch (error) {
    return false;
  }
}

function isSafeSvgResourceUrl(value) {
  return (
    LOCAL_FRAGMENT.test(value) ||
    RASTER_DATA_URL.test(value) ||
    isSafeExternalStickerUrl(value)
  );
}

function sanitizeSvgAttribute(element, attribute) {
  const name = attribute.localName.toLowerCase();
  const namespace = attribute.namespaceURI || '';
  const value = attribute.value.trim();

  if (namespace === XMLNS_NAMESPACE) {
    return value === SVG_NAMESPACE || value === XLINK_NAMESPACE;
  }

  if (
    namespace &&
    !(
      (namespace === XLINK_NAMESPACE && name === 'href') ||
      (namespace === XML_NAMESPACE && ['lang', 'space'].includes(name))
    )
  ) {
    return false;
  }

  const allowXmlWhitespace = !['href', 'src', 'style'].includes(name);
  if (
    name.startsWith('on') ||
    containsUnsafeMarkupToken(value, allowXmlWhitespace)
  ) {
    return false;
  }

  if (name === 'style') {
    const style = sanitizeStyle(
      value,
      ALLOWED_SVG_STYLE_PROPERTIES,
      validateSvgStyleValue
    );
    if (!style) {
      return false;
    }
    attribute.value = style;
    return true;
  }

  if (name === 'href' || name === 'src') {
    return EXTERNAL_SVG_RESOURCE_ELEMENTS.has(element.localName.toLowerCase())
      ? isSafeSvgResourceUrl(value)
      : LOCAL_FRAGMENT.test(value);
  }

  if (name === 'mask') {
    return isAllowedKeyword(value, 'none') || LOCAL_CSS_URL_VALUE.test(value);
  }

  return !CSS_URL_FUNCTION.test(value) || areOnlyLocalCssUrls(value);
}

export function sanitizeSvg(svg, { includeDimensions = false } = {}) {
  if (
    typeof svg !== 'string' ||
    !svg.trim() ||
    svg.length > MAX_INLINE_SVG_LENGTH ||
    !canSanitizeMarkup()
  ) {
    return '';
  }

  const fragment = DOMPurify.sanitize(parseInertMarkup(svg), SVG_CONFIG);
  const root = fragment.firstElementChild;
  const hasNonWhitespaceRootText = Array.from(fragment.childNodes).some(
    (node) => node.nodeType === 3 && node.textContent.trim()
  );
  if (
    fragment.children.length !== 1 ||
    hasNonWhitespaceRootText ||
    !root ||
    root.localName.toLowerCase() !== 'svg' ||
    root.namespaceURI !== SVG_NAMESPACE
  ) {
    return '';
  }

  for (const element of [root, ...root.querySelectorAll('*')]) {
    if (
      element.namespaceURI !== SVG_NAMESPACE ||
      !ALLOWED_SVG_ELEMENT_SET.has(element.localName.toLowerCase())
    ) {
      return '';
    }

    for (const attribute of Array.from(element.attributes)) {
      if (!sanitizeSvgAttribute(element, attribute)) {
        element.removeAttributeNode(attribute);
      }
    }
  }

  if (includeDimensions) {
    root.setAttribute('width', '100%');
    root.setAttribute('height', '100%');
  }

  return root.outerHTML;
}
